import {
  addToast,
  Button,
  Checkbox,
  Dialogue,
  Fieldset,
  Form,
  Label,
  PasswordField,
  Text
} from '@albert-io/atomic';
import type {PropsWithChildren} from 'react';
import React, { useEffect} from 'react';

import type {AppError} from 'lib/normalizeError';
import {useResetPasswordStore} from './ResetPassword.store';

/**
 * ResetPassword
 *
 * This component is not to be confused with ChangePassword**
 *
 * ResetPassword is used in the ResetPasswordModal component, which teachers
 * and administrators use to clear and reset students' passwords.
 *
 * It is also a dependency of the ChangePassword* family of components.
 *
 * The ResetPassword component's main purpose is to expose a set of primitives,
 * --mainly the "New Password" field and "Confirm Password" fields--which are
 * hooked into the same ResetPassword store.
 *
 * Thus the primitives come with their validation behaviors built in, including
 * enabling/disabling the form's "Save" button until all password criteria are
 * satisfied.
 *
 * By extending the ResetPassword store to other children, additional
 * criteria can be added at any time (e.g. ChangePassword's "Old Password" field).
 *
 * @see https://github.com/albert-io/albert-marketplace/pull/9062
 *
 * Props:
    customDisableSubmitCondition - prop to override the enabling/disabling of the
      "Save" button
    onCancel - click handler added to the "Cancel" button. If undefined, we hide the
      "Cancel" button
    onError - request error handler, takes an Error() object instance
    onSubmit - request handler that runs in our "Save" button click handler. Takes the
      new password as an argument and must return a Promise.
    onSubmitSuccess - an additional callback that runs on successful resolution
      of the onSubmit function.
    title: The text that will appear in the dialogue's header
 */

interface NewPasswordFieldProps {
  [key: string]: any;
}

const NewPasswordField = (props: NewPasswordFieldProps) => {
  const inputType = useResetPasswordStore((state) => state.inputType);
  const handlePasswordChange = useResetPasswordStore((state) => state.handlePasswordChange);
  const isPasswordValid = useResetPasswordStore((state) => state.isPasswordValid);
  const passwordInputValue = useResetPasswordStore((state) => state.passwordInputValue);

  const hasError = !!(passwordInputValue && !isPasswordValid);
  return (
    <PasswordField
      className='mb-x2'
      error={hasError}
      label='New password'
      message={
        hasError
          ? 'Password must have at least 8 characters and at least one number or special character.'
          : ''
      }
      onChange={handlePasswordChange}
      type={inputType}
      value={passwordInputValue}
      {...props}
    />
  );
};

interface ConfirmPasswordFieldProps {
  [key: string]: any;
}

const ConfirmPasswordField = (props: ConfirmPasswordFieldProps) => {
  const confirmInputValue = useResetPasswordStore((state) => state.confirmInputValue);
  const doPasswordsMatch = useResetPasswordStore((state) => state.doPasswordsMatch);
  const handleConfirmPasswordChange = useResetPasswordStore(
    (state) => state.handleConfirmPasswordChange
  );
  const inputType = useResetPasswordStore((state) => state.inputType);

  const hasError = !!(confirmInputValue && !doPasswordsMatch);
  return (
    <PasswordField
      className='mb-x2'
      error={hasError}
      label='Confirm new password'
      message={hasError ? 'Passwords do not match' : ''}
      onChange={handleConfirmPasswordChange}
      type={inputType}
      value={confirmInputValue}
      {...props}
    />
  );
};

const ShowPasswordToggle = () => {
  const setInputType = useResetPasswordStore((state) => state.setInputType);
  return (
    <Label>
      <Checkbox aria-hidden={false} className='mr-x2' onChange={setInputType} />
      Show password
    </Label>
  );
};

const PasswordForm = ({children}: PropsWithChildren<{}>) => {
  return (
    <Form onSubmit={(e) => e.preventDefault()}>
      <Fieldset className='flex flex-col'>{children}</Fieldset>
    </Form>
  );
};

const Legend = ({children}: PropsWithChildren<{}>) => (
  <Text as='legend' className='mb-x5' color='secondary'>
    {children}
  </Text>
);

interface ResetPasswordProps extends PropsWithChildrenRequired {
  customDisableSubmitCondition?: boolean;
  onCancel?: () => void;
  onError?: (error: any) => void;
  onSubmit: (password: string) => Promise<void>;
  onSubmitSuccess?: () => void;
  title?: string;
}

const defaultErrorHandler = (_error: AppError) => {
  addToast({
    title: 'Oops! Looks like something went wrong',
    color: 'negative',
    message: 'There was a problem with your request. If the problem persists, contact us.'
  });
};

const ResetPassword = ({
  customDisableSubmitCondition = false,
  onCancel,
  onError = defaultErrorHandler,
  onSubmit,
  onSubmitSuccess = () => {},
  title = 'Reset password',
  children,
  ...rest
}: ResetPasswordProps) => {
  const disableSubmit = useResetPasswordStore((state) => state.disableSubmit);
  const handleSubmit = useResetPasswordStore((state) => state.handleSubmit);
  const setCustomDisableSubmitCondition = useResetPasswordStore(
    (state) => state.setCustomDisableSubmitCondition
  );
  const reset = useResetPasswordStore((state) => state.reset);

  // Sync custom disable condition prop with store
  useEffect(() => {
    setCustomDisableSubmitCondition(customDisableSubmitCondition);
  }, [customDisableSubmitCondition, setCustomDisableSubmitCondition]);

  // Reset store when component unmounts
  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  const onSubmitClick = async () => {
    await handleSubmit({onSubmit, onError, onSubmitSuccess});
  };

  return (
    <Dialogue title={title} {...rest}>
      <Dialogue.Body>{children}</Dialogue.Body>
      <Dialogue.BtnGroup>
        <Button disabled={disableSubmit} onClick={onSubmitClick}>
          Update password
        </Button>
        {typeof onCancel === 'function' && (
          <Button color='secondary' onClick={onCancel}>
            Cancel
          </Button>
        )}
      </Dialogue.BtnGroup>
    </Dialogue>
  );
};

ResetPassword.ConfirmPasswordField = ConfirmPasswordField;
ResetPassword.Form = PasswordForm;
ResetPassword.NewPasswordField = NewPasswordField;
ResetPassword.ShowPasswordToggle = ShowPasswordToggle;
ResetPassword.Legend = Legend;

export default ResetPassword;
